import { compose, isEmpty, path, pathOr, reduce, uniq, values } from 'ramda'
import SMSHelper from 'smshelper'

import validate, { hasPzURLError, hasRequiredError } from '@/app/service/validate'

import { hasBody, validateJSON } from './api-helpers'
import { getActions } from './component/item/steps/content/types/multimessage/content'
import { hasWaFormEditorValidationErrors } from './component/wa-flow-editor-utils'
import { ERROR_MISSING_T0, MAX_VOICE_RETRIES, smsMaxLength } from './definitions'
import {
  CAMPAIGN_NO_CONTENT,
  ContactSelectionType,
  hasContent,
  LanguageType,
  NO_SENDER_ID_TEXT,
  reduceIndexed,
  SenderIdsForDripType,
  SMSBLAST_NO_CONTENT_TEXT,
} from './helpers'
import { getMessagePlaceholderLabel } from './label-helpers'
import { areSenderIdsSet } from './message-type-helpers'
import { isReminderCampaign } from './models/reminder/transform'
import {
  CampaignTemplateType,
  ContentAudio,
  MultimessageItem,
  OnAnswerType,
  OnTimeout,
  Part,
  PartFamily,
  SMSBlastCampaignItem,
} from './types'
import { validateOpenEndedFormat } from './utils/open-ended-validation'
import { getPartFamily, isPartQuestion, isVoicePart, toMessage } from './utils/part-content'
import { getVoicePartTranslation } from './utils/part-content/voice'
import { validateWhatsAppButtons, validateWhatsAppList } from './whatsapp-helpers'

export const validateTopupAmounts = (
  desiredAmount: string | number | null = '',
  maxAmount: string | number | null = '',
) => {
  const desired = String(desiredAmount ?? '')
  const max = String(maxAmount ?? '')
  const isNonPositive = (value: string) => !!value.trim() && Number.isFinite(Number(value)) && Number(value) <= 0
  const maxBelowDesired =
    Number.isFinite(Number(desired)) && Number.isFinite(Number(max)) && Number(max) > 0 && Number(desired) > Number(max)
  let maxAmountError = ''
  if (!max.trim()) {
    maxAmountError = 'Specify a maximum amount.'
  } else if (isNonPositive(max)) {
    maxAmountError = 'Maximum amount must be positive.'
  } else if (maxBelowDesired) {
    maxAmountError = 'Maximum amount must be greater than or equal to desired amount.'
  }

  return {
    desiredAmount: isNonPositive(desired) ? 'Desired amount must be positive.' : '',
    maxAmount: maxAmountError,
  }
}

export const checkCampaignForValidationErrors = ({
  item,
  senderIdsForDrip,
}: {
  item: MultimessageItem | SMSBlastCampaignItem | undefined
  contacts?: ContactSelectionType
  senderIdsForDrip: SenderIdsForDripType
}) => {
  if (!item) {
    return {
      isValid: true,
      errorMessages: [],
    }
  }

  const { type } = item
  if (type === 'smsblast') {
    const errorMessages = []
    let isValid = true

    if (!path(['variables', 'message', 'text'], item)) {
      isValid = false
      errorMessages.push(SMSBLAST_NO_CONTENT_TEXT)
    }
    if (!areSenderIdsSet({ item, availableSenderIds: senderIdsForDrip })) {
      isValid = false
      errorMessages.push(NO_SENDER_ID_TEXT)
    }

    return {
      isValid,
      errorMessages,
    }
  }
  const languages = pathOr([], ['uiStore', 'languages'], item) as LanguageType[]
  const defaultLanguage = pathOr('', ['uiStore', 'defaultLanguage'], item)

  const checkParts = (messages: Part[] = []): string[] =>
    reduceIndexed(
      (acc: string[], message: Part, id: number) => {
        const errorMessages: string[] = []

        if (!languages.length) {
          const errorMessage = validateContent(message)
          if (errorMessage) {
            errorMessages.push(errorMessage)
          }
        } else {
          const errorsMap: Record<string, { message: string; languages: string[] }> = {}
          for (let i = 0; i < languages.length; i += 1) {
            const language = languages[i]
            let errorMessage = ''
            // only check for default language of non voice parts
            if (defaultLanguage === language.value) {
              errorMessage = validateContent(message)
            } else if (isVoicePart(message)) {
              const content = getVoicePartTranslation(message, language.value)
              if (content) {
                errorMessage = validateTranslatedVoiceContent(message, content)
              }
            }

            if (errorMessage) {
              errorsMap[errorMessage] = {
                ...pathOr({}, [errorMessage], errorsMap),
                message: errorMessage,
                languages: [...pathOr([], [errorMessage, 'languages'], errorsMap), language.name],
              }
            }
          }

          const errors = Object.values(errorsMap)
          for (let i = 0; i < errors.length; i += 1) {
            const error = errors[i]

            const errorMessage = `${error.message} (Language${error.languages.length > 1 ? 's' : ''}: ${error.languages.join(', ')})`
            errorMessages.push(errorMessage)
          }
        }

        if (errorMessages.length) {
          const messageType = getPartFamily(message)
          const hasQuestion = isPartQuestion(message)
          const { label } = getMessagePlaceholderLabel(messageType, hasQuestion)
          acc.push(
            `${Number(id) + 1}. ${errorMessages
              .map((errorMessage) => `${message.label || label} ${errorMessage}`)
              .join('. ')}`,
          )
        }
        return acc
      },
      [],
      messages,
    )

  const parts = pathOr([], ['variables', 'parts'], item)
  const validSenderIds = areSenderIdsSet({ item, availableSenderIds: senderIdsForDrip })

  const errorMessages = checkParts(parts)

  if (!parts.length) {
    errorMessages.push(CAMPAIGN_NO_CONTENT)
  }

  if (!validSenderIds) {
    errorMessages.push(NO_SENDER_ID_TEXT)
  }
  if (isReminderCampaign(item) && !item.variables.defaultArgs?.t0) {
    errorMessages.push(ERROR_MISSING_T0)
  }

  if (type === CampaignTemplateType.CATI) {
    const { userStatuses } = item
    const error = validateUserStatuses(userStatuses || [])
    if (error) {
      errorMessages.push(error)
    }
  }

  const hasQuotas = Boolean(item.quotas?.length)

  if (hasQuotaActionsPart(parts) && !hasQuotas) {
    errorMessages.push(MISSING_QUOTAS_ERROR)
  }
  if (hasQuotas && !hasRunQuotaAction(parts)) {
    errorMessages.push(MISSING_RUN_QUOTA_ACTION_ERROR)
  }

  return {
    isValid: errorMessages.length === 0,
    errorMessages,
  }
}

export const validateHeaders = (headers: Record<string, string[]>) => {
  if (headers['']) {
    return 'has an empty header name'
  }

  const headerKeys = Object.keys(headers)
  for (let i = 0; i < headerKeys.length; i += 1) {
    const key = headerKeys[i]
    const headerValues = headers[key]
    if (headerValues.length === 0) {
      return `has no value for header '${key}'`
    }
    if (headerValues.includes('')) {
      return `has an empty value for header '${key}'`
    }
  }

  return ''
}

export const hasChoices = (onAnswer: OnAnswerType[]) =>
  reduce(
    (accum: boolean, { replies = [], ranges = [], reply }) => {
      const hasReplies = replies.length > 0 || ranges.length > 0 || !!reply
      return accum && hasReplies
    },
    true,
    onAnswer,
  )

const getNormalizedAnswerTexts = ({ replies = [], reply }: OnAnswerType) => {
  const answerTexts = Array.isArray(replies) ? replies : [replies]
  return [...answerTexts, reply]
    .filter((answerText): answerText is string => typeof answerText === 'string')
    .map((answerText) => answerText.trim().toLowerCase())
    .filter(Boolean)
}

export const hasDuplicatedAnswerText = (onAnswer: OnAnswerType[]) => {
  const previousChoices = new Set<string>()

  for (let i = 0; i < onAnswer.length; i += 1) {
    const answerTexts = new Set(getNormalizedAnswerTexts(onAnswer[i]))

    for (const answerText of answerTexts) {
      if (previousChoices.has(answerText)) {
        return true
      }
    }

    for (const answerText of answerTexts) {
      previousChoices.add(answerText)
    }
  }

  return false
}

export const hasRanges = (onAnswer: OnAnswerType[]) => {
  for (let i = 0; i < onAnswer.length; i += 1) {
    const { ranges } = onAnswer[i]
    if (ranges?.length) {
      return true
    }
  }
  return false
}

// basic check of a campaign part (SMS Question, WhatsApp Message, etc)
// returns empty string if valid, or an error message of the first error found
export const validateContent = (content: Part): string => {
  const item = toMessage(content)
  switch (item.type) {
    // action
    case PartFamily.Action:
    case PartFamily.QuotaActions:
      return ''

    case PartFamily.API: {
      const {
        apiCall: { method, url, contentType, headers, body },
      } = item

      if (!url) {
        return 'has no URL'
      }

      const invalidUrl = validate(hasRequiredError, hasPzURLError)(url)
      if (invalidUrl) {
        return 'has an invalid URL'
      }

      if (hasBody(method)) {
        if (!body) {
          return 'has no body'
        }

        if (contentType === 'application/json' && !validateJSON(body)) {
          return 'has invalid JSON in body'
        }
      }

      return validateHeaders(headers || {})
    }

    // CATI
    case PartFamily.CATI: {
      if (item.hasQuestion && item.onAnswer && !hasChoices(item.onAnswer)) {
        return '- all choices must have a reply'
      }
      if (item.hasQuestion && item.onAnswer && hasDuplicatedAnswerText(item.onAnswer)) {
        return '- answer choices must be unique'
      }
      if (!hasContent(pathOr({}, ['message'], item), 'text', false)) {
        return 'has no content'
      }
      return ''
    }

    // SMS
    case PartFamily.SMS: {
      if (item.hasQuestion) {
        const validationError = validateOpenEndedFormat(item.openEnded)
        if (validationError) {
          return `- ${validationError}`
        }
      }
      if (item.hasQuestion && item.onAnswer && !hasChoices(item.onAnswer)) {
        return '- all choices must have a reply text'
      }
      if (item.hasQuestion && item.onAnswer && hasDuplicatedAnswerText(item.onAnswer)) {
        return '- answer choices must be unique'
      }
      if (item.hasQuestion && item.onAnswer && item.useAi && hasRanges(item.onAnswer)) {
        return '- ranges are not supported with AI yet'
      }
      if (!hasContent(pathOr({}, ['message'], item), 'text', false)) {
        return 'has no text'
      }
      const parts = SMSHelper.parts(pathOr('', ['message', 'text'], item))
      if (parts > smsMaxLength) {
        return 'is too long'
      }
      return (item.hasQuestion && item.onTimeout && validateTimeout(item.onTimeout)) || ''
    }

    case PartFamily.Topup: {
      const { desiredAmount, maxAmount } = ('topup' in item && item.topup) || {}
      const errors = validateTopupAmounts(desiredAmount, maxAmount)
      return String(maxAmount ?? '').trim() ? errors.maxAmount || errors.desiredAmount : 'has no max amount'
    }

    // voice
    case PartFamily.Voice: {
      if (item.hasQuestion && item.onAnswer) {
        if (item.speechSettings) {
          const speechLanguages = (item.speechSettings.languages || {}) as Record<string, string>
          const languages = compose<[Record<string, string>], string[], string[]>(uniq, values)(speechLanguages)
          for (let i = 0; i < item.onAnswer.length; i += 1) {
            let hasSpeechReplies = false
            const { speechReplies } = item.onAnswer[i]
            for (let j = 0; j < languages.length; j += 1) {
              const language = languages[j]
              if (speechReplies?.[language]?.length) {
                hasSpeechReplies = true
                break
              }
            }

            if (!hasSpeechReplies) {
              return '- all choices must have at least one speech recognition word configured'
            }
          }
        } else if (!hasChoices(item.onAnswer)) {
          return '- all choices must have a keypress or range'
        } else if (hasDuplicatedAnswerText(item.onAnswer)) {
          return '- answer choices must be unique'
        }
      }

      if (item.hasQuestion && item.speechSettings && hasAudioFileWithoutTranscription(item.audio)) {
        return 'has no transcription'
      }

      const voiceErr = validateVoiceContent(item.audio)
      if (voiceErr) {
        return voiceErr
      }
      if (item.hasQuestion && Number(item.retries) > MAX_VOICE_RETRIES) {
        return `has too many retries (max ${MAX_VOICE_RETRIES})`
      }
      return (item.hasQuestion && item.onTimeout && validateTimeout(item.onTimeout)) || ''
    }

    // whatsapp
    case PartFamily.WhatsApp: {
      if (item.hasQuestion) {
        const validationError = validateOpenEndedFormat(item.openEnded)
        if (validationError) {
          return `- ${validationError}`
        }
        if (item.onAnswer && !hasChoices(item.onAnswer)) {
          return '- all choices must have a reply text'
        }
        if (item.onAnswer && hasDuplicatedAnswerText(item.onAnswer)) {
          return '- answer choices must be unique'
        }
        if (item.onAnswer && item.useAi && hasRanges(item.onAnswer)) {
          return '- ranges are not supported with AI yet'
        }
        if (item.onButtons) {
          const errorMessage = validateWhatsAppButtons(item.onButtons)
          if (errorMessage) {
            return errorMessage
          }
        }
        if (item.onList) {
          const errorMessage = validateWhatsAppList(item.onList)
          if (errorMessage) {
            return errorMessage
          }
        }
      }

      const valid =
        pathOr(0, ['message', 'text', 'length'], item) > 0 ||
        pathOr(0, ['message', 'image', 'length'], item) > 0 ||
        pathOr(0, ['message', 'audio', 'length'], item) > 0 ||
        pathOr(0, ['message', 'video', 'length'], item) > 0 ||
        pathOr(0, ['message', 'document', 'length'], item) > 0
      if (!valid) {
        return 'has no text, image, audio, video or document'
      }
      return (item.hasQuestion && item.onTimeout && validateTimeout(item.onTimeout)) || ''
    }

    case PartFamily.WhatsAppFlow: {
      return hasWaFormEditorValidationErrors(item.flow) ? 'has invalid configuration' : ''
    }

    default:
      return '' // unhandled, but nothing to do
  }
}

export const hasAudioFileWithoutTranscription = (voiceContent: ContentAudio): boolean =>
  !!voiceContent.playfile && !voiceContent.transcription?.trim()

const validateTranslatedVoiceContent = (message: Part, content: ContentAudio): string => {
  const item = toMessage(message)
  if (
    item.type === PartFamily.Voice &&
    item.hasQuestion &&
    item.speechSettings &&
    hasAudioFileWithoutTranscription(content)
  ) {
    return 'has no transcription'
  }
  return validateVoiceContent(content, true)
}

export const validateTimeout = (onTimeout: OnTimeout) => {
  const { timeout, timeoutSeconds } = onTimeout
  if (typeof timeoutSeconds === 'number') {
    if (timeoutSeconds < 0) {
      return 'timeout cannot be negative'
    }
    if (timeoutSeconds >= 1000) {
      return 'timeout cannot be greater than 999 seconds' // got it from backend
    }
  } else if (timeout) {
    const { unit, value } = timeout

    if (value < 0) {
      return 'timeout cannot be negative'
    }

    if (value && !unit) {
      return 'timeout must have a unit'
    }
    if (!value && unit) {
      return 'timeout cannot be zero'
    }
  }
  return ''
}

export const validateVoiceContent = (voiceContent: ContentAudio, ignoreAudio = false): string => {
  const hasSay = voiceContent.say
  const hasVoice = voiceContent.voice

  if (hasSay) {
    if (!hasVoice) {
      return 'has no selected voice for Text-to-Speech'
    }
  } else if (hasVoice) {
    return 'has no message for Text-to-Speech'
  } else if (!ignoreAudio && !hasContent(voiceContent, 'playfile', false)) {
    return 'has no audio'
  }

  return ''
}

export const hasValidTTSContent = (voiceContent: ContentAudio): boolean => {
  const { voice, say, translations } = voiceContent
  if (!voice || !say) {
    return false
  }

  return (
    !translations ||
    isEmpty(translations) ||
    Object.values(translations).every(
      (translation) => (!translation.voice && !translation.say) || (translation.voice && translation.say),
    )
  )
}

export const validateUserStatuses = (userStatuses: string[]): string => {
  if (!userStatuses.length) {
    return ''
  }

  const trimmed = userStatuses.map((status) => status.trim())
  if (trimmed.some((status) => !status)) {
    return 'User status must not be empty'
  }

  if (trimmed.some((status) => status.length > 100)) {
    return 'User status must not exceed 100 characters'
  }

  if (trimmed.length !== new Set(trimmed).size) {
    return 'User status must be unique'
  }

  return ''
}

const MISSING_RUN_QUOTA_ACTION_ERROR = 'Campaigns with quotas must include a Mark Survey as Completed action.'
const MISSING_QUOTAS_ERROR = 'Campaigns with Quota Actions must have at least one quota.'

const hasQuotaActionsPart = (parts: Part[]): boolean => parts.some((part) => 'quotaActions' in part)

const hasRunQuotaAction = (parts: Part[]): boolean => parts.flatMap(getActions).some((action) => 'runQuota' in action)
